import { buildTenantAbsoluteUrl } from "./tenantPreview";

export const DEFAULT_SHARING_CONFIG = {
  enabled: true,
  allowOutgoingPosts: true,
  allowOutgoingMessages: true,
  allowOutgoingMedia: true,
  allowIncomingPosts: true,
  allowIncomingMessages: true,
  allowIncomingMedia: true,
  supportedApps: [
    "whatsapp",
    "telegram",
    "facebook",
    "instagram",
    "snapchat",
    "slack",
    "linkedin",
    "tiktok",
  ],
};

export function normalizeSharingConfig(raw) {
  const sharing = raw?.sharing || raw || {};
  const supportedApps = Array.isArray(sharing.supportedApps)
    ? [...new Set(sharing.supportedApps.map((entry) => `${entry || ""}`.trim().toLowerCase()).filter(Boolean))]
    : DEFAULT_SHARING_CONFIG.supportedApps;

  return {
    ...DEFAULT_SHARING_CONFIG,
    ...sharing,
    supportedApps:
      supportedApps.length > 0
        ? supportedApps
        : DEFAULT_SHARING_CONFIG.supportedApps,
  };
}

export function toAbsoluteUrl(value) {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return buildTenantAbsoluteUrl(value.startsWith("/") ? value : `/${value}`);
}

function compactParts(parts) {
  return parts
    .map((value) => `${value || ""}`.trim())
    .filter(Boolean);
}

function resolveSiteName(siteName) {
  return `${siteName || ""}`.trim() || "Uniflo";
}

function resolveAuthorName(author) {
  const firstName = author?.firstName || author?.first_name || "";
  const lastName = author?.lastName || author?.last_name || "";
  return `${firstName} ${lastName}`.trim() || author?.username || "Uniflo user";
}

function resolvePostPrimaryMedia(post) {
  if (Array.isArray(post?.images) && post.images.length > 0) {
    return toAbsoluteUrl(post.images[0]);
  }
  if (Array.isArray(post?.attachments) && post.attachments.length > 0) {
    return toAbsoluteUrl(post.attachments[0]?.url || "");
  }
  if (post?.sharedPost) {
    return resolvePostPrimaryMedia(post.sharedPost);
  }
  return "";
}

export function buildPostSharePayload(post, options = {}) {
  const postId = post?._id || post?.id || post?.guid || "";
  const url = postId ? toAbsoluteUrl(`/post/${postId}`) : toAbsoluteUrl("/");
  const authorName = resolveAuthorName(post?.author || post?.user || {});
  const content = `${post?.content || post?.text || ""}`.trim();
  const mediaUrl = resolvePostPrimaryMedia(post);
  const siteName = resolveSiteName(options.siteName);

  return {
    title: `${authorName} on ${siteName}`,
    text: compactParts([content, mediaUrl, url]).join("\n\n"),
    url,
  };
}

export function buildMessageSharePayload({
  message,
  conversationId,
  conversationLabel,
  siteName,
  includeAttachments = true,
}) {
  const messageId = message?._id || message?.id || "";
  const threadUrl = conversationId
    ? toAbsoluteUrl(
        `/messages/${conversationId}${messageId ? `?message=${messageId}` : ""}`
      )
    : toAbsoluteUrl("/messages");
  const firstAttachment = includeAttachments && Array.isArray(message?.attachments)
    ? toAbsoluteUrl(message.attachments[0]?.url || "")
    : "";
  const label = `${conversationLabel || `${resolveSiteName(siteName)} conversation`}`.trim();
  const content = `${message?.content || ""}`.trim();

  return {
    title: label,
    text: compactParts([content || `Shared from ${label}`, firstAttachment, threadUrl]).join(
      "\n\n"
    ),
    url: threadUrl,
  };
}

// T-054 (B3) — navigator.share()'s promise rejects with AbortError when the
// person dismisses the native sheet, and in some environments (observed on
// uniflo.app/epwp, 2026-09-20: a caller's button stuck on "Sharing..." for
// over two minutes) never settles at all. Race it against this so a caller
// awaiting shareExternally is always guaranteed to get a result.
const SHARE_TIMEOUT_MS = 15000;

function shareTimeout(ms) {
  return new Promise((_, reject) => {
    setTimeout(() => {
      const error = new Error("Sharing timed out.");
      error.name = "ShareTimeoutError";
      reject(error);
    }, ms);
  });
}

export async function shareExternally(payload, options = {}) {
  const title = `${payload?.title || ""}`.trim();
  const text = `${payload?.text || ""}`.trim();
  const url = `${payload?.url || ""}`.trim();
  const fallbackText = compactParts([title, text, url]).join("\n\n");
  const timeoutMs = Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : SHARE_TIMEOUT_MS;

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await Promise.race([
        navigator.share({
          ...(title ? { title } : {}),
          ...(text ? { text } : {}),
          ...(url ? { url } : {}),
        }),
        shareTimeout(timeoutMs),
      ]);
      return "shared";
    } catch (error) {
      // A dismiss (AbortError) and a share sheet that never told us either
      // way (our own timeout, above) both mean "nothing to report" -- quiet
      // either way, and NOT a reason to fall through to a clipboard copy the
      // person never asked for. Any other rejection is a real failure and
      // propagates to the caller instead of being swallowed here.
      if (error?.name === "AbortError" || error?.name === "ShareTimeoutError") {
        return "cancelled";
      }
      throw error;
    }
  }

  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    await navigator.clipboard.writeText(fallbackText);
    return "copied";
  }

  if (typeof document !== "undefined" && typeof document.execCommand === "function") {
    const textarea = document.createElement("textarea");
    textarea.value = fallbackText;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      if (document.execCommand("copy")) {
        return "copied";
      }
    } finally {
      textarea.remove();
    }
  }

  throw new Error("Sharing is unavailable in this browser.");
}
